import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import { toast } from 'sonner'

import type {
    EndTripRequest,
    ForceEndTripRequest,
    ListTripsParams,
} from '@/lib/api/delivery-trip-api'
import { deliveryTripApi } from '@/lib/api/delivery-trip-api'
import { vehicleKeys } from '@/hooks/use-vehicles-query'
import { m } from '@/lib/paraglide/messages'

export const tripKeys = {
    all: ['delivery-trips'] as const,
    active: () => [...tripKeys.all, 'active'] as const,
    activeByVehicle: (vehicleId: string) =>
        [...tripKeys.all, 'active-by-vehicle', vehicleId] as const,
    activeByDriver: (driverId: string) =>
        [...tripKeys.all, 'active-by-driver', driverId] as const,
    lists: () => [...tripKeys.all, 'list'] as const,
    list: (params: ListTripsParams) => [...tripKeys.lists(), params] as const,
    detail: (id: string) => [...tripKeys.all, 'detail', id] as const,
}

/**
 * useActiveTripByVehicle — trip đang chạy của 1 vehicle cụ thể.
 * `enabled` cho phép caller defer fetch cho đến khi vehicle detail load xong
 * (tránh 404 lúc detail còn undefined). Trả `null` khi vehicle không có
 * active trip — component chỉ render section khi data khác null.
 */
export function useActiveTripByVehicle(vehicleId: string, enabled = true) {
    return useQuery({
        queryKey: tripKeys.activeByVehicle(vehicleId),
        queryFn: async () => {
            try {
                const res = await deliveryTripApi.getActiveByVehicle(vehicleId)
                return res.data ?? null
            } catch (err) {
                if (err instanceof AxiosError && err.response?.status === 404) {
                    return null
                }
                throw err
            }
        },
        enabled: enabled && Boolean(vehicleId),
        staleTime: 30_000,
        refetchOnWindowFocus: true,
        refetchInterval: 60_000,
    })
}

/**
 * Chuyến đang chạy của MỘT tài xế cụ thể — dùng khi văn phòng bấm "bắt đầu
 * giao hàng" hộ bác tài. Hỏi `useActiveTrip` (chuyến của chính mình) sẽ luôn ra
 * rỗng và bắt người bấm chọn xe, trong khi bác tài có thể đang chạy dở chuyến
 * khác — backend khi đó trả TRIP_VEHICLE_MISMATCH.
 */
export function useDriverActiveTrip(driverId: string, enabled = true) {
    return useQuery({
        queryKey: tripKeys.activeByDriver(driverId),
        queryFn: async () => {
            try {
                const res = await deliveryTripApi.getActiveByDriver(driverId)
                return res.data ?? null
            } catch (err) {
                if (err instanceof AxiosError && err.response?.status === 404) {
                    return null
                }
                throw err
            }
        },
        enabled: enabled && Boolean(driverId),
        staleTime: 30_000,
    })
}

export function useActiveTrip(enabled = true) {
    return useQuery({
        queryKey: tripKeys.active(),
        queryFn: async () => {
            try {
                const res = await deliveryTripApi.getActive()
                return res.data ?? null
            } catch (err) {
                if (err instanceof AxiosError && err.response?.status === 404) {
                    return null
                }
                throw err
            }
        },
        enabled,
        staleTime: 30_000,
        refetchOnWindowFocus: true,
        refetchInterval: 60_000,
    })
}

export function useEndActiveTrip() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: (data?: EndTripRequest) => deliveryTripApi.endActive(data),
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: tripKeys.active() })
            void qc.invalidateQueries({ queryKey: tripKeys.lists() })
            void qc.invalidateQueries({ queryKey: vehicleKeys.available() })
            void qc.invalidateQueries({ queryKey: vehicleKeys.lists() })
            toast.success(m.trip_toast_ended())
        },
        onError: (err) => {
            const code =
                err instanceof AxiosError
                    ? err.response?.data?.error?.code
                    : undefined
            if (code === 'TRIP_HAS_OPEN_ORDERS') {
                toast.error(m.trip_error_has_open_orders())
            } else if (code === 'NO_ACTIVE_TRIP') {
                toast.error(m.trip_error_no_active())
            } else {
                toast.error(m.common_save_error())
            }
        },
    })
}

/**
 * useForceEndTrip — admin cleanup khi tài xế quên end trip. Note bắt buộc
 * (BE reject nếu < 3 ký tự). Invalidate cả trip active-by-vehicle của trip
 * đó (nếu biết vehicleId) để card "Chuyến đang chạy" ẩn ngay sau khi close.
 * Caller truyền `vehicleId` optional để invalidate targeted query key.
 */
export function useForceEndTrip(vehicleId?: string) {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: ({
            tripId,
            data,
        }: {
            tripId: string
            data: ForceEndTripRequest
        }) => deliveryTripApi.forceEnd(tripId, data),
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: tripKeys.all })
            void qc.invalidateQueries({ queryKey: vehicleKeys.available() })
            void qc.invalidateQueries({ queryKey: vehicleKeys.lists() })
            if (vehicleId) {
                void qc.invalidateQueries({
                    queryKey: tripKeys.activeByVehicle(vehicleId),
                })
                void qc.invalidateQueries({
                    queryKey: vehicleKeys.detail(vehicleId),
                })
            }
            toast.success(m.trip_force_end_success())
        },
        onError: (err) => {
            const code =
                err instanceof AxiosError
                    ? err.response?.data?.error?.code
                    : undefined
            if (code === 'TRIP_HAS_ACTIVE_DELIVERIES') {
                toast.error(m.trip_force_end_error_has_in_delivery())
            } else if (code === 'TRIP_ALREADY_CLOSED') {
                toast.error(m.trip_force_end_error_already_closed())
            } else {
                toast.error(m.common_save_error())
            }
        },
    })
}

export function useDeliveryTrips(params?: ListTripsParams) {
    return useQuery({
        queryKey: tripKeys.list(params ?? {}),
        queryFn: async () => {
            const res = await deliveryTripApi.list(params)
            return { data: res.data ?? [], meta: res.meta }
        },
    })
}

export function useDeliveryTrip(id: string) {
    return useQuery({
        queryKey: tripKeys.detail(id),
        queryFn: async () => {
            const res = await deliveryTripApi.getById(id)
            return res.data ?? null
        },
        enabled: Boolean(id),
    })
}
